/**
 * Le regole di un obbligo: frequenza di aggiornamento, natura, durata di
 * pubblicazione dei documenti.
 *
 * Perche' esiste
 * --------------
 * Sono tre cose che il catalogo ANAC non dice e il Comune sa. Si correggevano
 * solo passando dalle Allerte, cioe' **dopo** che l'ipotesi aveva gia' prodotto
 * un'allerta; qui si impostano dove si guardano i termini, prima che scadano.
 *
 * Tre domande diverse, e la modale le tiene separate perche' il Comune le
 * confondeva: la **frequenza di aggiornamento** dice ogni quanto l'obbligo va
 * rinnovato; la **durata di pubblicazione** dice per quanto ciascun documento
 * resta online prima di passare in archivio. «Annuale» e «cinque anni» non
 * rispondono alla stessa domanda.
 *
 * Ciascuna e' facoltativa — si dichiara cio' che si sa —, ma non tutte vuote.
 *
 * Perche' la natura non si propone
 * --------------------------------
 * Se una regola c'e' gia', la natura arriva compilata e basta confermarla. Se non
 * c'e', **non si propone un valore**: la natura spegne per sempre una classe di
 * allerte, ed e' una scelta che va fatta leggendo, non accettata per inerzia —
 * lo stesso principio di `ModaleVerdetto`. Si puo' lasciare «non dichiarata».
 *
 * La scrittura e' riservata al Super Admin, e la verifica l'API: la pagina non
 * offre il comando agli altri ruoli.
 */

import { useState } from "react";
import type { ReactNode } from "react";
import { ErroreApi } from "../api/client";
import { CADENZE_OBBLIGO, NATURE_OBBLIGO } from "../api/types";
import type {
  CadenzaObbligo,
  NaturaObbligo,
  RegolaObbligo,
  RichiestaRegola,
} from "../api/types";
import { ETICHETTA_NATURA } from "../lib/etichette";
import {
  DURATA_MASSIMA_ANNI,
  DURATA_MINIMA_ANNI,
  DURATA_PREDEFINITA_ANNI,
  ETICHETTE_CADENZA,
  testoProvenienzaCadenza,
} from "../lib/scadenze";
import type { CadenzaRisolta } from "../lib/scadenze";
import { CampoLibero, CampoScelta } from "./campi";
import type { OpzioneScelta } from "./campi";
import { Modale } from "./Modale";
import { Avviso, Bottone } from "./primitive";

/** Lunghezza minima del motivo, la stessa che l'API impone. */
const MOTIVO_MINIMO = 10;

const OPZIONI_DURATA: OpzioneScelta[] = Array.from(
  { length: DURATA_MASSIMA_ANNI - DURATA_MINIMA_ANNI + 1 },
  (_, indice) => {
    const anni = DURATA_MINIMA_ANNI + indice;
    return { valore: String(anni), etichetta: `${anni} ${anni === 1 ? "anno" : "anni"}` };
  },
);

export function ModaleRegoleObbligo({
  denominazione,
  contesto,
  corrente,
  regola,
  onInvia,
  onChiudi,
  onCompletato,
  oltreLaFineCon,
}: {
  /** Nome dell'obbligo, per il titolo. */
  denominazione: string;
  /** Percorso dell'obbligo, per non confondere le denominazioni ripetute. */
  contesto?: ReactNode;
  /** La frequenza che si sta applicando oggi, con la sua provenienza. */
  corrente: CadenzaRisolta;
  /** La dichiarazione in vigore, se il Comune ne ha gia' fatta una. */
  regola?: RegolaObbligo;
  onInvia: (dati: RichiestaRegola) => Promise<RegolaObbligo>;
  onChiudi: () => void;
  /** Riceve la regola registrata, con quanti documenti ha gia' portato oltre la fine. */
  onCompletato: (registrata: RegolaObbligo) => void;
  /**
   * Quanti documenti dell'obbligo sarebbero gia' oltre la fine con una durata
   * dell'obbligo diversa (`documentiOltreLaFineCon`): l'anteprima di cio' che
   * uscira' dalla pagina salvando.
   */
  oltreLaFineCon?: (durataAnni: number | null) => number;
}) {
  const iniziali: { cadenza: CadenzaObbligo | ""; natura: NaturaObbligo | ""; durata: string } = {
    cadenza: regola?.cadenza_dichiarata ?? "",
    natura: regola?.natura ?? "",
    durata: regola?.durata_anni ? String(regola.durata_anni) : "",
  };
  const [cadenza, setCadenza] = useState<CadenzaObbligo | "">(iniziali.cadenza);
  const [natura, setNatura] = useState<NaturaObbligo | "">(iniziali.natura);
  const [durata, setDurata] = useState(iniziali.durata);
  const [motivo, setMotivo] = useState("");
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  const cambiaQualcosa =
    cadenza !== iniziali.cadenza || natura !== iniziali.natura || durata !== iniziali.durata;
  // Una regola tutta vuota l'API la rifiuta: «non dichiaro nulla» non e' una
  // dichiarazione.
  const dichiaraQualcosa = cadenza !== "" || natura !== "" || durata !== "";
  const motivoCorto = motivo.trim().length < MOTIVO_MINIMO;
  // Cambiata la durata, l'API archivia subito cio' che finisce oltre: lo si
  // dice prima di salvare, non dopo.
  const usciranno =
    durata !== iniziali.durata && oltreLaFineCon ? oltreLaFineCon(durata ? Number(durata) : null) : 0;
  const pronto = cambiaQualcosa && dichiaraQualcosa && !motivoCorto;

  const invia = async () => {
    if (!pronto) return;
    setErrore(null);
    setInCorso(true);
    try {
      const registrata = await onInvia({
        natura: natura || null,
        cadenza_dichiarata: cadenza || null,
        durata_anni: durata ? Number(durata) : null,
        motivo: motivo.trim(),
      });
      onCompletato(registrata);
    } catch (causa) {
      setErrore(
        causa instanceof ErroreApi
          ? causa.messaggioEsteso
          : "Non e' stato possibile dichiarare le regole dell'obbligo.",
      );
    } finally {
      setInCorso(false);
    }
  };

  // Durante l'invio non si chiude: la dichiarazione arriverebbe comunque, e
  // l'avviso di esito comparirebbe dopo un «Annulla».
  const chiudi = () => {
    if (!inCorso) onChiudi();
  };

  return (
    <Modale
      titolo={`Regole di «${denominazione}»`}
      aperta
      onChiudi={chiudi}
      pedice={
        <>
          <Bottone variante="fantasma" onClick={chiudi} disabled={inCorso}>
            Annulla
          </Bottone>
          <Bottone
            variante="primario"
            inCorso={inCorso}
            disabled={!pronto}
            onClick={() => void invia()}
          >
            Dichiara le regole
          </Bottone>
        </>
      }
    >
      <div className="pila">
        {contesto ? <div>{contesto}</div> : null}

        {errore ? (
          <Avviso tono="critico" titolo="Dichiarazione non riuscita">
            {errore}
          </Avviso>
        ) : null}

        <div>
          <p className="anteprima__etichetta">Oggi</p>
          <p>
            Aggiornamento {ETICHETTE_CADENZA[corrente.cadenza].toLowerCase()} —{" "}
            {testoProvenienzaCadenza(corrente.fonte, corrente.id_nodo_fonte)}. Documenti
            pubblicati per{" "}
            {regola?.durata_anni
              ? `${regola.durata_anni} anni (regola dell'obbligo)`
              : `${DURATA_PREDEFINITA_ANNI} anni (art. 8 c. 3)`}
            , salvo una durata scelta per il singolo documento.
          </p>
          {regola ? (
            <p className="campo__aiuto">
              Dichiarazione in vigore di {regola.dichiarata_da}, {regola.aggiornata_il}: «
              {regola.motivo}».
            </p>
          ) : null}
        </div>

        <CampoScelta
          etichetta="Frequenza di aggiornamento"
          valore={cadenza}
          onCambia={(valore) => setCadenza(valore as CadenzaObbligo | "")}
          opzioni={CADENZE_OBBLIGO.map((voce) => ({
            valore: voce,
            etichetta: ETICHETTE_CADENZA[voce],
          }))}
          vuoto="Non dichiarata: si tiene l'ipotesi della sezione"
          aiuto="Ogni quanto l'obbligo va rinnovato. Il termine di aggiornamento si calcola dall'ultimo aggiornamento dell'obbligo, qui e nel cruscotto."
        />

        <CampoScelta
          etichetta="Durata di pubblicazione dei documenti"
          valore={durata}
          onCambia={setDurata}
          opzioni={OPZIONI_DURATA}
          vuoto={`Non dichiarata: ${DURATA_PREDEFINITA_ANNI} anni (art. 8 c. 3)`}
          aiuto="Per quanto ogni documento resta nella pubblicazione corrente prima di passare in archivio. Vale per i documenti che non ne hanno una propria; si puo' cambiare documento per documento."
        />
        {usciranno > 0 ? (
          <Avviso tono="attenzione" titolo="Salvando, alcuni documenti escono subito">
            {usciranno === 1
              ? "Con questa durata 1 documento dell'obbligo e' gia' oltre la fine: salvando esce subito dalla pagina e passa nell'archivio, dove resta accessibile ai suoi indirizzi."
              : `Con questa durata ${usciranno} documenti dell'obbligo sono gia' oltre la fine: salvando escono subito dalla pagina e passano nell'archivio, dove restano accessibili ai loro indirizzi.`}
          </Avviso>
        ) : null}

        <CampoScelta
          etichetta="Natura dell'obbligo"
          valore={natura}
          onCambia={(valore) => setNatura(valore as NaturaObbligo | "")}
          opzioni={NATURE_OBBLIGO.map((voce) => ({
            valore: voce,
            etichetta: ETICHETTA_NATURA[voce],
          }))}
          vuoto="Non dichiarata"
          aiuto={
            regola?.natura
              ? "Quella gia' dichiarata: si puo' confermare cosi' com'e'."
              : "Facoltativa. Dice se i contenuti si sostituiscono o si accumulano, e spegne per sempre le allerte che su questo obbligo non hanno senso."
          }
        />

        <CampoLibero
          etichetta="Motivo"
          valore={motivo}
          onCambia={setMotivo}
          obbligatorio
          multilinea
          errore={
            motivo.length > 0 && motivoCorto
              ? `Almeno ${MOTIVO_MINIMO} caratteri: resta agli atti, ed e' l'unica spiegazione disponibile a chi rileggera'.`
              : null
          }
          aiuto="Resta agli atti con nome e data: indicare la norma o la prassi dell'ente, non «visto»."
        />
      </div>
    </Modale>
  );
}
